import React from "react";
import { ArrowLeft, Shield } from "lucide-react";
import { Button } from "../components/ui/button";

const HEADER_LOGO_URL = "/header_logo.jpeg";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src={HEADER_LOGO_URL} alt="Kavach Logo" className="h-14 w-14 object-contain rounded-full" />
            <div>
              <h1 className="text-xl font-bold" style={{color: '#003366'}}>KAVACH INFRA SOLUTIONS LLP</h1>
              <p className="text-xs" style={{color: '#D4AF37', fontWeight: 600}}>रक्षा | सुरक्षा | सजगता</p>
            </div>
          </div>
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
            style={{borderColor: '#003366', color: '#003366'}}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-10 h-10" style={{color: '#003366'}} />
            <h1 className="text-4xl font-bold" style={{color: '#003366'}}>Privacy Policy</h1>
          </div>
          <p className="text-slate-600">Last Updated: November 2, 2025</p>
        </div>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>1. Introduction</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Welcome to Kavach Infra Solutions LLP ("Kavach", "we", "our", or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose and safeguard your information when you use our website www.kavachinfra.in and our AI-powered fire safety compliance platform, Kavach FireSight Pro.
            </p>
            <p className="text-slate-700 leading-relaxed">
              By accessing or using our services, you acknowledge that you have read, understood and agree to be bound by this Privacy Policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>2.1 Personal Information</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When you register for our services, we collect:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Name and email address (via Google OAuth authentication)</li>
              <li>Profile picture (from your Google account)</li>
              <li>Company or organization details (if provided)</li>
              <li>Phone number (if provided for support)</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>2.2 Technical Files and Documents</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When you use Kavach FireSight Pro, we collect:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Construction drawings, floor plans and technical documents you upload</li>
              <li>PDF files and images for fire safety analysis</li>
              <li>Analysis results, Bill of Quantities (BOQ) and compliance reports</li>
              <li>Chat messages and queries sent to our AI assistant</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3" style={{color: '#003366'}}>2.3 Usage Information</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We automatically collect certain information when you use our services:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>IP address, browser type and operating system</li>
              <li>Pages visited, time spent and features used</li>
              <li>Login timestamps and session information</li>
              <li>Activity logs for security and analytics purposes</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>3. How We Use Your Information</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li><strong>Service Delivery:</strong> To provide AI-powered fire safety compliance analysis and generate NBC, NFPA and BIS compliant reports</li>
              <li><strong>User Authentication:</strong> To verify your identity and manage your account through Google OAuth</li>
              <li><strong>Admin Approval:</strong> To review and approve user access requests by our admin team</li>
              <li><strong>Communication:</strong> To send approval notifications, welcome emails and important updates via info@kavachinfra.in</li>
              <li><strong>Improvement:</strong> To analyze usage patterns, improve our AI models and enhance user experience</li>
              <li><strong>Support:</strong> To respond to your inquiries and provide technical assistance</li>
              <li><strong>Security:</strong> To detect, prevent and address technical issues, fraud or security threats</li>
              <li><strong>Compliance:</strong> To comply with legal obligations and enforce our terms and conditions</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>4. AI and Machine Learning</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Kavach FireSight Pro utilizes advanced AI technology:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li><strong>AI Vision Technology:</strong> We use advanced machine learning models to analyze your uploaded documents and generate compliance reports</li>
              <li><strong>Data Processing:</strong> Your files are processed by AI systems to extract fire safety information, detect compliance gaps and generate BOQs</li>
              <li><strong>Training:</strong> We do not use your uploaded files to train our AI models without your explicit consent</li>
              <li><strong>Quality:</strong> AI-generated reports are provided as guidance and should be reviewed by qualified professionals</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>5. Data Sharing and Disclosure</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We do not sell your personal information. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li><strong>Service Providers:</strong> With third-party vendors who assist us (Google OAuth, Email delivery services)</li>
              <li><strong>Admin Team:</strong> Your registration details are shared with our admin team (kavachinfrasolutions@gmail.com and info@kavachinfra.in) for approval purposes</li>
              <li><strong>Legal Compliance:</strong> When required by law, court order or government authority</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition or sale of assets, with prior notice to you</li>
              <li><strong>Consent:</strong> With your explicit consent for any other purpose</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>6. Data Security</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>SSL/TLS encryption for all data transmission</li>
              <li>Secure storage of files and documents in MongoDB database</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Regular security audits and monitoring</li>
              <li>Admin approval workflow to control user access</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>7. Data Retention</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We retain your information for as long as necessary to:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Provide our services and maintain your account</li>
              <li>Comply with legal obligations and resolve disputes</li>
              <li>Maintain analysis history and reports for your records</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              You may request deletion of your account and associated data by contacting us at info@kavachinfra.in. Some information may be retained for legal or legitimate business purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>8. Your Rights</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Objection:</strong> Object to processing of your information for certain purposes</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service provider</li>
              <li><strong>Withdrawal:</strong> Withdraw consent for data processing (where consent is the legal basis)</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              To exercise any of these rights, please contact us at info@kavachinfra.in with your request.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>9. Cookies and Tracking</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We use cookies and similar tracking technologies to:
            </p>
            <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4">
              <li>Maintain your session and keep you logged in</li>
              <li>Remember your preferences and settings</li>
              <li>Analyze usage patterns and improve our services</li>
              <li>Provide a personalized user experience</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              You can control cookies through your browser settings. However, disabling cookies may affect your ability to use certain features of our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>10. Children's Privacy</h2>
            <p className="text-slate-700 leading-relaxed">
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately at info@kavachinfra.in and we will take steps to delete such information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>11. International Data Transfers</h2>
            <p className="text-slate-700 leading-relaxed">
              Your information may be transferred to and processed in countries other than India. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable data protection laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>12. Changes to This Privacy Policy</h2>
            <p className="text-slate-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website and updating the "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>13. Contact Us</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you have any questions, concerns or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-50 p-6 rounded-lg border-l-4" style={{borderColor: '#D4AF37'}}>
              <p className="font-semibold mb-2" style={{color: '#003366'}}>Kavach Infra Solutions LLP</p>
              <p className="text-slate-700 mb-1"><strong>Email:</strong> info@kavachinfra.in</p>
              <p className="text-slate-700 mb-1"><strong>Phone:</strong> +91 9890838368 | +91 9593843999</p>
              <p className="text-slate-700 mb-1"><strong>Corporate Office:</strong></p>
              <p className="text-slate-700 pl-4">1304, 13th Floor, A Wing, Dev Corpora,</p>
              <p className="text-slate-700 pl-4">Eastern Express Highway, Opp. Cadbury Junction,</p>
              <p className="text-slate-700 pl-4">Thane West, Maharashtra 400601, India</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
